import _uniq from 'lodash-es/uniq';
import flat from 'flat';

// The attribute paths a Meilisearch hit matched on. With `attributesToHighlight` set, `_formatted` wraps each
// match in <em>, so flattening it turns { policyDetails: { cars: [{ VIN: '<em>123</em>' }] } } into
// ['policyDetails.cars.0.VIN'].
export const highlightedAttributePaths = (hit) => {
  if (!hit?._formatted) {
    return [];
  }
  const flattened = flat(hit._formatted);
  return Object.keys(flattened).filter(
    (path) => typeof flattened[path] === 'string' && flattened[path].includes('<em>')
  );
};

const POLICY_ATTRIBUTE_FRIENDLY_NAME = {
  firstName: 'first name',
  lastName: 'last name',
  cars: 'Car VIN',
  dateOfBirth: 'DOB',
  accountId: 'Account ID',
  id: 'Policy ID',
  state: 'State',
  mortgageHolderName: 'Mortgage Lender',
  status: 'Status'
};

// Policy hits highlight every attribute, so a path is labelled by any friendly-name key it contains.
const policyAttributeLabels = (path) =>
  Object.keys(POLICY_ATTRIBUTE_FRIENDLY_NAME)
    .filter((attribute) => path.includes(attribute))
    .map((attribute) => {
      const label = POLICY_ATTRIBUTE_FRIENDLY_NAME[attribute];
      if (attribute === 'firstName' || attribute === 'lastName') {
        return path.includes('drivers') ? `Driver ${label}` : `Person ${label}`;
      }
      return label;
    });

export const policyMatchedOn = (hit) => _uniq(highlightedAttributePaths(hit).flatMap(policyAttributeLabels)).join(', ');

// Customer and offer searches highlight only the attributes they search on, so these are exact paths
// (array indexes removed).
const CUSTOMER_ATTRIBUTE_LABELS = {
  'attributes.name': 'First name',
  'attributes.family_name': 'Last name',
  Username: 'Account ID',
  'attributes.address': 'Address',
  'attributes.custom_city': 'City',
  'attributes.custom_state': 'State',
  'attributes.custom_zip': 'ZIP code',
  'attributes.phone_number': 'Phone',
  'attributes.custom_addl_phone_numbers': 'Phone',
  'attributes.email': 'Email',
  'attributes.custom_affinity_code': 'Affinity code'
};

const OFFER_ATTRIBUTE_LABELS = {
  'quote.fname': 'First name',
  'quote.lname': 'Last name',
  'quote.drivers.firstName': 'Driver first name',
  'quote.drivers.lastName': 'Driver last name',
  'quote.submittedAddress.address': 'Address',
  'quote.submittedAddress.city': 'City',
  'quote.submittedAddress.state': 'State',
  'quote.submittedAddress.zip': 'ZIP code',
  'quote.rep': 'Offer rep',
  webUserId: 'Account ID',
  'quote.email': 'Email',
  'quote.phone': 'Phone',
  'quote.global.affinity': 'Affinity code'
};

const matchedOnFrom = (labels) => (hit) =>
  _uniq(
    highlightedAttributePaths(hit)
      .map((path) => labels[path.replace(/\.\d+(?=\.|$)/g, '')])
      .filter(Boolean)
  ).join(', ');

export const customerMatchedOn = matchedOnFrom(CUSTOMER_ATTRIBUTE_LABELS);
export const offerMatchedOn = matchedOnFrom(OFFER_ATTRIBUTE_LABELS);
